import React, { useState, useEffect, useRef, useReducer, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Chip,
  Tooltip,
  Skeleton,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ImageSearch,
  Refresh,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Photo as PhotoIcon,
  BrokenImage as BrokenImageIcon,
  GridView as GridIcon,
  ZoomIn as DetailIcon,
  ViewCompact as ThumbnailIcon,
  CloudDownload as DownloadIcon,
  AutoFixHigh as OptimizeIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { io, Socket } from 'socket.io-client';
import authInstance from '../../services/httpClient';
import config from '../../config';
import { getStoredToken } from '../../services/tokenRefreshService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Progress {
  created: number;
  active: number;
  completed: number;
  failed: number;
  total: number;
}

interface Stats {
  totalImages: number;
  missingOriginal: number;
  missingGrid: number;
  missingDetail: number;
  missingThumbnail: number;
  fullyProcessed: number;
  missingSub: number;
  noAttachment: number;
}

const EMPTY_PROGRESS: Progress = { created: 0, active: 0, completed: 0, failed: 0, total: 0 };

interface Props {
  onBack: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatEta(seconds: number): string {
  if (seconds <= 0) return '';
  if (seconds > 3600) {
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    return `~${h}h ${m}m remaining`;
  }
  if (seconds > 60) return `~${Math.round(seconds / 60)}m remaining`;
  return `~${seconds}s remaining`;
}

// ---------------------------------------------------------------------------
// Stat Card
// ---------------------------------------------------------------------------

function StatCard({
  icon,
  label,
  value,
  color,
  loading,
  missingType,
  includeAll,
}: {
  icon: React.ReactElement;
  label: string;
  value: number;
  color: string;
  loading?: boolean;
  missingType?: string;
  includeAll?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [fetchingProducts, setFetchingProducts] = useState(false);

  const handleClick = async () => {
    if (!missingType || value === 0) return;
    if (expanded) { setExpanded(false); return; }
    setFetchingProducts(true);
    try {
      const { default: authInstance } = await import('../../services/httpClient');
      const { data } = await authInstance.get(`admin/product-attachments/missing-products?type=${missingType}&includeAll=${includeAll || false}&limit=200`);
      setProducts(data.products || []);
    } catch { setProducts([]); }
    setFetchingProducts(false);
    setExpanded(true);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        flex: '1 1 0', minWidth: 130,
        cursor: missingType && value > 0 ? 'pointer' : 'default',
        '&:hover': missingType && value > 0 ? { borderColor: color, boxShadow: 1 } : {},
      }}
      onClick={handleClick}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ color, display: 'flex' }}>{icon}</Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
            {label} {missingType && value > 0 && <span style={{ fontSize: 10, opacity: 0.6 }}>(click to view)</span>}
          </Typography>
          {loading ? (
            <Skeleton width={40} height={28} />
          ) : (
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {value.toLocaleString()}
            </Typography>
          )}
        </Box>
      </CardContent>
      {expanded && (
        <Box sx={{ px: 2, pb: 1.5, maxHeight: 200, overflowY: 'auto', borderTop: '1px solid', borderColor: 'divider' }}>
          {fetchingProducts ? (
            <CircularProgress size={16} sx={{ my: 1 }} />
          ) : products.length > 0 ? (
            products.map((p: any) => (
              <Typography key={p.id} variant="caption" sx={{ display: 'block', py: 0.25, fontFamily: 'monospace' }}>
                {p.sku} — {p.name}
              </Typography>
            ))
          ) : (
            <Typography variant="caption" color="text.disabled">No products found</Typography>
          )}
        </Box>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ImageProcessing: React.FC<Props> = ({ onBack }) => {
  const notify = useNotify();

  const [loading, setLoading] = useState<'missing' | 'redownload' | 'reprocess' | null>(null);
  const [confirmReprocess, setConfirmReprocess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Stats
  // -------------------------------------------------------------------------
  const [stats, setStats] = useState<Stats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [includeAll, setIncludeAll] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const params = includeAll ? '?includeAll=true' : '';
      const response = await authInstance.get(`/admin/product-attachments/stats${params}`);
      setStats(response.data?.data || response.data);
    } catch {
      // best-effort
    } finally {
      setStatsLoading(false);
    }
  }, [includeAll]);

  // -------------------------------------------------------------------------
  // Progress tracking (ref + batched renders via rAF)
  // -------------------------------------------------------------------------
  const progressRef = useRef<Progress>({ ...EMPTY_PROGRESS });
  const [, forceRender] = useReducer((x: number) => x + 1, 0);
  const rafRef = useRef<number | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const etaRef = useRef({ count: 0, startTime: 0 });

  const scheduleRender = useCallback(() => {
    if (rafRef.current == null) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        forceRender();
      });
    }
  }, [forceRender]);

  const setProgressFromServer = useCallback(
    (data: Partial<Progress>) => {
      progressRef.current = {
        created: data.created ?? 0,
        active: data.active ?? 0,
        completed: data.completed ?? 0,
        failed: data.failed ?? 0,
        total: data.total ?? 0,
      };
      scheduleRender();
    },
    [scheduleRender],
  );

  const handleProgressEvent = useCallback(
    (data: { status?: string; total?: number; completed?: number; [key: string]: any }) => {
      if (data.total != null && data.completed != null) {
        setProgressFromServer(data);
        return;
      }
      const prev = progressRef.current;
      if (data.status === 'completed') {
        progressRef.current = { ...prev, completed: prev.completed + 1, created: Math.max(0, prev.created - 1) };
      } else if (data.status === 'failed') {
        progressRef.current = { ...prev, failed: prev.failed + 1, created: Math.max(0, prev.created - 1) };
      }
      scheduleRender();
    },
    [scheduleRender, setProgressFromServer],
  );

  const fetchProgress = useCallback(async () => {
    try {
      const response = await authInstance.get('/admin/product-attachments/progress');
      const data = response.data?.data || response.data || {};
      setProgressFromServer(data);
    } catch {
      // best-effort
    }
  }, [setProgressFromServer]);

  // -------------------------------------------------------------------------
  // Socket.IO lifecycle
  // -------------------------------------------------------------------------
  useEffect(() => {
    fetchProgress();
    fetchStats();
    const socketUrl = config.API_URL.replace(/\/v\d+$/, '');
    const token = getStoredToken();
    const socket = io(socketUrl, { auth: { token }, transports: ['websocket'] });
    socketRef.current = socket;
    socket.on('connect', () => socket.emit('admin:subscribe', { room: 'image-processing' }));
    socket.on('image-processing:progress', handleProgressEvent);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      socket.emit('admin:unsubscribe', { room: 'image-processing' });
      socket.off('image-processing:progress', handleProgressEvent);
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch stats when toggle changes
  useEffect(() => {
    setStatsLoading(true);
    fetchStats();
  }, [includeAll, fetchStats]);

  // -------------------------------------------------------------------------
  // Derived values
  // -------------------------------------------------------------------------
  const progress = progressRef.current;
  const processed = progress.completed + progress.failed;
  const isActive = progress.total > 0 && processed < progress.total;
  const isDone = progress.total > 0 && processed >= progress.total;
  const percent = progress.total > 0 ? Math.round((processed / progress.total) * 100) : 0;
  const remaining = progress.total - processed;

  // ETA
  if (isActive && etaRef.current.startTime === 0) {
    etaRef.current = { count: progress.completed, startTime: Date.now() };
  } else if (!isActive) {
    etaRef.current = { count: 0, startTime: 0 };
  }
  const elapsed = (Date.now() - etaRef.current.startTime) / 1000;
  const recentCompleted = progress.completed - etaRef.current.count;
  const rate = elapsed > 10 ? recentCompleted / elapsed : 0;
  const etaSeconds = rate > 0 ? Math.round(remaining / rate) : 0;
  const etaDisplay = formatEta(etaSeconds);
  const rateDisplay = rate > 0 ? `${rate.toFixed(1)} images/sec` : '';

  // -------------------------------------------------------------------------
  // Action handlers
  // -------------------------------------------------------------------------
  const handleProcessMissingSub = async () => {
    setLoading('missing');
    setError(null);
    setSuccessMessage(null);
    try {
      const params = includeAll ? '?includeAll=true' : '';
      const response = await authInstance.post(`/admin/product-attachments/cache${params}`);
      const { queued } = response.data?.data || response.data || {};
      const msg = queued != null
        ? `Queued ${queued.toLocaleString()} images with missing sizes for processing.`
        : 'Missing images have been queued for processing.';
      setSuccessMessage(msg);
      notify(msg, { type: 'success' });
      await fetchProgress();
      await fetchStats();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to process missing images';
      setError(msg);
      notify(msg, { type: 'error' });
    } finally {
      setLoading(null);
    }
  };

  const handleRedownloadMissing = async () => {
    setLoading('redownload');
    setError(null);
    setSuccessMessage(null);
    try {
      const params = includeAll ? '?includeAll=true' : '';
      const response = await authInstance.post(`/admin/product-attachments/cache${params}`);
      const { queued } = response.data?.data || response.data || {};
      const msg = queued != null
        ? `Queued ${queued.toLocaleString()} images for re-download from Vend.`
        : 'Missing images have been queued for download.';
      setSuccessMessage(msg);
      notify(msg, { type: 'success' });
      await fetchProgress();
      await fetchStats();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to queue re-downloads';
      setError(msg);
      notify(msg, { type: 'error' });
    } finally {
      setLoading(null);
    }
  };

  const handleReprocessAll = async () => {
    setConfirmReprocess(false);
    setLoading('reprocess');
    setError(null);
    setSuccessMessage(null);
    try {
      const params = includeAll ? '?includeAll=true' : '';
      const response = await authInstance.post(`/admin/product-attachments/reprocess${params}`);
      const { queued } = response.data?.data || response.data || {};
      const msg = queued != null
        ? `Queued ${queued.toLocaleString()} images for full reprocessing.`
        : 'All images have been queued for reprocessing.';
      setSuccessMessage(msg);
      notify(msg, { type: 'success' });
      await fetchProgress();
      await fetchStats();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to reprocess images';
      setError(msg);
      notify(msg, { type: 'error' });
    } finally {
      setLoading(null);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <IconButton onClick={onBack}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
          Image Processing
        </Typography>
        <Tooltip title="Refresh stats">
          <IconButton
            onClick={() => { setStatsLoading(true); fetchStats(); fetchProgress(); }}
            disabled={statsLoading}
          >
            <Refresh sx={{ animation: statsLoading ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 6 }}>
        Manage product image processing. Each image is downloaded from Vend, then converted into
        three optimized sizes for the app.
      </Typography>

      {/* Size chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, ml: 6, flexWrap: 'wrap' }}>
        <Tooltip title="480x480 WebP — used in product grids and search results">
          <Chip label="Grid  480x480" size="small" variant="outlined" />
        </Tooltip>
        <Tooltip title="1200x1200 WebP — used on product detail pages">
          <Chip label="Detail  1200x1200" size="small" variant="outlined" />
        </Tooltip>
        <Tooltip title="272px height, original format — legacy thumbnail for older clients">
          <Chip label="Thumbnail  272px" size="small" variant="outlined" />
        </Tooltip>
      </Box>

      {/* Scope toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, ml: 6 }}>
        <ToggleButtonGroup
          value={includeAll ? 'all' : 'app'}
          exclusive
          size="small"
          onChange={(_, val) => { if (val) setIncludeAll(val === 'all'); }}
        >
          <ToggleButton value="app" sx={{ textTransform: 'none', px: 2 }}>
            In-App Products
          </ToggleButton>
          <ToggleButton value="all" sx={{ textTransform: 'none', px: 2 }}>
            All Products
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          {includeAll ? 'Showing all products including inactive/non-sellable' : 'Showing only active, sellable, in-app products'}
        </Typography>
      </Box>

      {/* ---- Stats cards ---- */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, ml: 6, flexWrap: 'wrap' }}>
        <StatCard
          icon={<PhotoIcon />}
          label="Total Images"
          value={stats?.totalImages ?? 0}
          color="#0288d1"
          loading={statsLoading}
        />
        <StatCard
          icon={<CheckCircleIcon />}
          label="Fully Processed"
          value={stats?.fullyProcessed ?? 0}
          color="#2e7d32"
          loading={statsLoading}
        />
        <StatCard
          icon={<BrokenImageIcon />}
          label="Missing Original"
          value={stats?.missingOriginal ?? 0}
          color="#d32f2f"
          loading={statsLoading}
          missingType="original"
          includeAll={includeAll}
        />
        <StatCard
          icon={<GridIcon />}
          label="Missing Grid"
          value={stats?.missingGrid ?? 0}
          color="#ed6c02"
          loading={statsLoading}
          missingType="grid"
          includeAll={includeAll}
        />
        <StatCard
          icon={<DetailIcon />}
          label="Missing Detail"
          value={stats?.missingDetail ?? 0}
          color="#ed6c02"
          loading={statsLoading}
          missingType="detail"
          includeAll={includeAll}
        />
        <StatCard
          icon={<ThumbnailIcon />}
          label="Missing Thumbnail"
          value={stats?.missingThumbnail ?? 0}
          color="#ed6c02"
          loading={statsLoading}
          missingType="thumbnail"
          includeAll={includeAll}
        />
        <StatCard
          icon={<WarningIcon />}
          label="No Attachment"
          value={stats?.noAttachment ?? 0}
          color="#d32f2f"
          loading={statsLoading}
          missingType="no_attachment"
          includeAll={includeAll}
        />
      </Box>

      {/* Auto-processing note */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 3, ml: 6 }}>
        <InfoIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
        <Typography variant="caption" color="text.disabled">
          Missing sub-images are automatically processed every night at 3:30 AM SAST.
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* ---- Live progress ---- */}
      {isActive && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Processing images
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Downloading originals, generating grid + detail + thumbnail, uploading to storage.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" fontWeight={500}>
                {progress.completed.toLocaleString()} of {progress.total.toLocaleString()} images
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {percent}%
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={percent}
              sx={{ height: 8, borderRadius: 4, mb: 1 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {remaining.toLocaleString()} remaining
                {progress.active > 0 && ` \u2022 ${progress.active} active workers`}
                {rateDisplay && ` \u2022 ${rateDisplay}`}
                {progress.failed > 0 && (
                  <Typography component="span" variant="caption" color="error.main" sx={{ ml: 0.5 }}>
                    ({progress.failed.toLocaleString()} failed)
                  </Typography>
                )}
              </Typography>
              {etaDisplay && (
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  {etaDisplay}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {isDone && (
        <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />} sx={{ mb: 3 }}>
          Processing complete: {progress.completed.toLocaleString()} succeeded
          {progress.failed > 0 ? `, ${progress.failed.toLocaleString()} failed` : ''} out of{' '}
          {progress.total.toLocaleString()} total.
        </Alert>
      )}

      {/* ---- Actions ---- */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Re-download missing from Vend */}
        <Card variant="outlined">
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, '&:last-child': { pb: 2 } }}>
            <DownloadIcon sx={{ fontSize: 32, color: '#d32f2f' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Re-download missing images from Vend
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Finds products with no original image stored and re-downloads them from the
                Vend image URL. Also generates all three sub-sizes.
              </Typography>
              {stats && stats.missingOriginal > 0 && (
                <Chip
                  label={`${stats.missingOriginal} missing`}
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
            <Button
              variant="contained"
              color="error"
              onClick={handleRedownloadMissing}
              disabled={loading !== null}
              startIcon={loading === 'redownload' ? <CircularProgress size={18} /> : <DownloadIcon />}
              sx={{ minWidth: 150, whiteSpace: 'nowrap' }}
            >
              {loading === 'redownload' ? 'Scanning...' : 'Re-download'}
            </Button>
          </CardContent>
        </Card>

        {/* Process missing sub-images */}
        <Card variant="outlined">
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, '&:last-child': { pb: 2 } }}>
            <ImageSearch sx={{ fontSize: 32, color: '#0288d1' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Process missing sub-images
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Finds products that have an original image but are missing grid, detail, or
                thumbnail sizes and generates them. Skips already processed images. Safe to run anytime.
              </Typography>
              {stats && stats.missingSub > 0 && (
                <Chip
                  label={`${stats.missingSub} need processing`}
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
            <Button
              variant="contained"
              onClick={handleProcessMissingSub}
              disabled={loading !== null}
              startIcon={loading === 'missing' ? <CircularProgress size={18} /> : <ImageSearch />}
              sx={{ minWidth: 150, whiteSpace: 'nowrap' }}
            >
              {loading === 'missing' ? 'Scanning...' : 'Process Missing'}
            </Button>
          </CardContent>
        </Card>

        {/* Regenerate all */}
        <Card variant="outlined">
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, '&:last-child': { pb: 2 } }}>
            <OptimizeIcon sx={{ fontSize: 32, color: '#ed6c02' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Regenerate all optimized images
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Re-downloads every product image, applies the latest processing pipeline,
                and regenerates all three sizes. Original source images in Vend are never modified.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <InfoIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.disabled">
                  Use after changing processing settings or to fix inconsistent images across all products.
                </Typography>
              </Box>
            </Box>
            {!confirmReprocess ? (
              <Button
                variant="outlined"
                color="warning"
                onClick={() => setConfirmReprocess(true)}
                disabled={loading !== null}
                startIcon={loading === 'reprocess' ? <CircularProgress size={18} /> : <Refresh />}
                sx={{ minWidth: 150, whiteSpace: 'nowrap' }}
              >
                {loading === 'reprocess' ? 'Queuing...' : 'Reprocess All'}
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={() => setConfirmReprocess(false)}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  onClick={handleReprocessAll}
                >
                  Confirm
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Box>
  );
};

export default ImageProcessing;
